import { NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from "@angular/core";
import { S25ItemComponent } from "../s25-item/s25.item.component";
import { S25VirtualGridAbstractItem } from "../s25-virtual-grid/s25.virtual.grid.abstract.item";
import { AcademicGrid } from "./s25.academic.grid";
import { Item } from "../../pojo/Item";
import { S25TimeBubbleComponent } from "../../standalone/s25.time.bubble.component";
import { Grid } from "../s25-virtual-grid/s25.virtual.grid";
import { AcademicGridStore } from "./s25.academic.grid.store";
import { VoidTemplateDirective } from "../../standalone/typed.template.directive";
import { S25AcademicGridItemTooltipComponent } from "./s25.academic.grid.item.tooltip.component";

type BubbleDirection = "up" | "down" | undefined;
type Bubbles = { setup: BubbleDirection; start: BubbleDirection; end: BubbleDirection; takedown: BubbleDirection };

@Component({
    standalone: true,
    selector: "s25-ng-academic-grid-item",
    host: {
        "[class.sortedByRelevance]": "sortedByRelevance()",
    },
    template: `
        <div
            class="item grid-color"
            [class.locationReserved]="item().academicGrid.displayAsConflict()"
            [attr.data-type]="gridColorType()"
            [attr.data-pattern]="colorPattern()"
            [class.isShadow]="projection().role === 'shadow'"
            [class.hasOverlap]="!!projection().overlappingItems().length"
            [class.dragging]="dragging()"
        >
            <!-- s25-item brings a popover, a context menu and two name resources per tile, which is far
                 too much to build for every visible event. The placeholder carries the same body, so the
                 tile paints immediately and only grows a tooltip once it is hovered or activated. -->
            @defer (on hover; on interaction) {
                <s25-item
                    [itemId]="item().academicGrid.eventId"
                    [itemTypeId]="Item.Ids.Event"
                    [itemName]="item().academicGrid.eventName"
                    [newTab]="true"
                    [hideLabel]="true"
                    [popoverTemplate]="popoverTemplate"
                >
                    <ng-container [ngTemplateOutlet]="eventBody" />
                </s25-item>
            } @placeholder {
                <div class="s25-item-holder" role="link" tabindex="0" [attr.aria-label]="item().academicGrid.eventName">
                    <ng-container [ngTemplateOutlet]="eventBody" />
                </div>
            }
        </div>

        <ng-template #eventBody>
            <div class="eventWrapper" (click)="$event.stopPropagation()">
                @if (projection().role === "shadow") {
                    <div class="itemContent">
                        <span class="textBackground">{{ item().academicGrid.effective.position.dow() }}</span>
                    </div>
                } @else if (projection().role === "related-location-conflict") {
                } @else {
                    @if (bubbles()?.setup; as bubbleDirection) {
                        <s25-ng-time-bubble [position]="bubbleDirection" [hours]="startHour()" />
                    }
                    @if (!!item().academicGrid.setup) {
                        <div class="additionalTime setup" [style.--part-hours]="item().academicGrid.setup / 60"></div>
                    }
                    @if (!!item().academicGrid.pre) {
                        <div class="additionalTime pre" [style.--part-hours]="item().academicGrid.pre / 60"></div>
                    }
                    @if (bubbles()?.start; as bubbleDirection) {
                        <s25-ng-time-bubble [position]="bubbleDirection" [hours]="eventStartHour()" />
                    }
                    <div class="itemContent"></div>
                    @if (bubbles()?.end; as bubbleDirection) {
                        <s25-ng-time-bubble [position]="bubbleDirection" [hours]="eventEndHour()" />
                    }
                    @if (!!item().academicGrid.post) {
                        <div class="additionalTime post" [style.--part-hours]="item().academicGrid.post / 60"></div>
                    }
                    @if (!!item().academicGrid.takedown) {
                        <div
                            class="additionalTime takedown"
                            [style.--part-hours]="item().academicGrid.takedown / 60"
                        ></div>
                    }
                    @if (bubbles()?.takedown; as bubbleDirection) {
                        <s25-ng-time-bubble [position]="bubbleDirection" [hours]="endHour()" />
                    }
                }
            </div>
        </ng-template>

        <ng-template #popoverTemplate void>
            <s25-ng-academic-grid-item-tooltip [data]="{ item: item() }" />
        </ng-template>
    `,
    styles: `
        .item {
            height: calc(100% - 3 * var(--border-width));
            translate: 0 var(--border-width);
            border: var(--grid-color--border, var(--item-border));
        }

        .item.isShadow {
            text-align: center;
            position: relative;
        }

        .item.hasOverlap .itemContent {
            text-align: left;
        }

        ::ng-deep s25-ng-academic-grid-item .item .s25-item-holder {
            width: 100%;
        }

        ::ng-deep s25-ng-academic-grid-item .item .ngInlineBlock,
        ::ng-deep s25-ng-academic-grid-item .item .ngInlineBlock > div,
        ::ng-deep s25-ng-academic-grid-item .item .s25-item-holder {
            height: 100%;
            display: block;
        }

        .eventWrapper {
            line-height: calc(var(--row-height) - 2 * var(--border-width));
            display: flex;
            height: 100%;
        }

        .eventWrapper > .additionalTime:where(:first-of-type, :last-of-type) {
            flex-basis: calc(var(--column-width) * var(--part-hours) - var(--border-width));
        }

        .item .itemContent {
            padding-inline: 0.25rem;
            flex: 1 1 0;
            /* Flex items default to min-width: auto, which blocks text-overflow: ellipsis */
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 0.9em;

            .textBackground {
                padding: 0.2em;
                padding-block-end: 0;
                background-color: var(--grid-color--background-color);
                border-radius: 3px;
            }
        }

        .additionalTime {
            background-color: rgb(255 255 255 / 40%);
            flex-shrink: 0;
            flex-grow: 0;
            flex-basis: calc(var(--column-width) * var(--part-hours));
        }

        .setup,
        .pre {
            border-inline-end: 1px dotted black;
        }

        .post,
        .takedown {
            border-inline-start: 1px dotted black;
        }

        .dragging {
            pointer-events: none;
        }

        :host.sortedByRelevance .item::after {
            --border-width: 3px;
            --gap: 3px;
            content: "";
            width: calc(100% + 2 * var(--gap));
            height: calc(100% + 2 * var(--gap));
            display: block;
            position: absolute;
            top: calc(-1 * var(--border-width) - var(--gap));
            left: calc(-1 * var(--border-width) - var(--gap));
            border: var(--border-width) red dashed;
            box-sizing: content-box;
            pointer-events: none;
        }

        .item[data-pattern] {
            --grid-color--pattern-height: calc(
                var(--row-height) - 2 * var(--item-border-width) - 3 * var(--border-width)
            );
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgTemplateOutlet,
        S25ItemComponent,
        S25TimeBubbleComponent,
        S25AcademicGridItemTooltipComponent,
        VoidTemplateDirective,
    ],
})
export class S25AcademicGridItemComponent
    implements S25VirtualGridAbstractItem<AcademicGrid.Item, AcademicGrid.ProjectionData>
{
    readonly item = input.required<AcademicGrid.Item>();
    readonly projection = input.required<Grid.ItemProjection<AcademicGrid.Item, AcademicGrid.ProjectionData>>();
    readonly dragging = input(false);
    readonly moving = input(false);
    readonly candidate = computed(() => this.item().academicGrid.effective);

    protected readonly dow = computed(() => this.item().academicGrid.effective.position.dow());
    protected readonly startHour = computed(() => this.item().academicGrid.effective.position.startHour());
    protected readonly endHour = computed(() => this.item().academicGrid.effective.position.endHour());
    protected readonly eventStartHour = computed(
        () => this.startHour() + (this.item().academicGrid.setup + this.item().academicGrid.pre) / 60,
    );
    protected readonly eventEndHour = computed(
        () => this.endHour() - (this.item().academicGrid.post + this.item().academicGrid.takedown) / 60,
    );
    protected readonly sortedByRelevance = computed(() => this.store.sortLocationsByItem() === this.item());

    protected readonly Item = Item;
    readonly store = inject(AcademicGridStore);

    readonly colorPattern = computed(() => {
        const warningsConfig = this.store.warningsConfig();
        const item = this.item();

        if (item.academicGrid?.displayAsConflict() || item.academicGrid?.isLocationConflict) return "none"; // Warnings never apply to conflicts
        const issueOrder = [
            "instructorConflicts",
            "locationConflicts",
            "resourceConflicts",
            "locationUndersized",
            "locationOversized",
            "missingFeaturePrefs",
            "missingPartitionPrefs",
            "standardSchedules",
        ] as const;
        for (const key of issueOrder) {
            if (warningsConfig[key].action !== "ignore" && item.academicGrid?.issues[key]?.()) {
                if (key === "standardSchedules") return "conflict";
                return key;
            }
        }

        return "none";
    });

    protected readonly gridColorType = computed(() => {
        const item = this.item();
        if (item.academicGrid?.displayAsConflict() || item.academicGrid?.isLocationConflict) {
            return "locationReserved";
        }
        if (this.projection().role === "shadow") return "blackout";
        if (this.projection().role === "related-location-conflict") return "locationReserved";
        if (item.academicGrid?.adHoc) return "closed";
        if (!!item.academicGrid?.effective.position.locations().length) return "eventWithLocations";
        return "event";
    });

    protected readonly bubbles = computed<Bubbles | undefined>(() => {
        if (!this.dragging()) return;

        const bubbles: Bubbles = { setup: "down", start: "up", end: "down", takedown: "up" };

        const atTop = this.projection().clippedPosition().top <= 0.001;
        const atBottom = this.projection().clippedPosition().top + this.projection().clippedPosition().height >= 99.999;

        if (this.projection().frozen || atBottom) {
            bubbles.setup = "up";
            bubbles.end = "up";
        } else if (atTop) {
            bubbles.start = "down";
            bubbles.takedown = "down";
        }

        if (!this.item().academicGrid.setup && !this.item().academicGrid.pre) bubbles.setup = undefined;
        if (!this.item().academicGrid.post && !this.item().academicGrid.takedown) bubbles.takedown = undefined;

        return bubbles;
    });
}
